// frontend/src/pages/ReceptionHistory.js
//
// What the client sees of a reception checklist.
//
// The workshop fills in seventeen fields at check-in; this page was rendering
// four damage badges and one comment. Everything else — the scratches noted in
// free text, the warning lights, the fuel level, the odometer, the keys, the
// client's own remarks, the reason the car came in — was already in the
// payload and simply never drawn.
//
// That matters more here than on most screens. This is the document the client
// is asked to CONFIRM, and confirming acknowledges the vehicle's condition as
// recorded. Being asked to sign off on a summary while the detail stays hidden
// is the wrong way round: the row stays compact for scanning, and every field
// is one click away.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import { fmt } from '../theme/iparc';
import { CheckCircle2, AlertCircle, ChevronDown } from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtDateTime = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? '—'
    : `${t.toLocaleDateString('fr-FR')} à ${t.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};
// fmt already folds fr-FR's narrow no-break space to a plain one.
const km = (n) => (n || n === 0 ? `${fmt(n)} km` : '—');

const CONDITION_FR = {
  Excellent: 'Excellent', Good: 'Bon', Fair: 'Moyen', Poor: 'Mauvais',
  Clean: 'Propre', Normal: 'Normal', Dirty: 'Sale',
  Worn: 'Usés', Damaged: 'Endommagés',
};
const fr = (v) => CONDITION_FR[v] || v || '—';

/** One label/value line. */
const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-[3px]">
    <span className="text-[11px] text-[#8F877B] shrink-0">{label}</span>
    <span className="text-[12.5px] text-[#F4F1EA] text-right min-w-0 break-words">{value}</span>
  </div>
);

/**
 * A block of free text the workshop wrote.
 *
 * Rendered even when empty, as "rien de signalé". On a document the client is
 * asked to sign, an explicitly empty section is information — it says nothing
 * was recorded, rather than leaving them to wonder whether something was left
 * out of the view.
 */
const NoteBlock = ({ title, text, tone = 'default' }) => (
  <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
    <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">{title}</p>
    {text ? (
      <p className={`text-[12.5px] leading-[1.55] whitespace-pre-wrap break-words ${
        tone === 'warn' ? 'text-[#DFA23C]' : 'text-[#F4F1EA]'
      }`}>
        {text}
      </p>
    ) : (
      <p className="text-[12px] text-[#5C554C] italic">Rien de signalé.</p>
    )}
  </div>
);

const ReceptionHistory = () => {
  const { token } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/checklist/owner/checklists`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChecklists(response.data);
    } catch (err) {
      setError("Impossible de charger l'historique de réception. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchChecklists();
    // eslint-disable-next-line
  }, [token]);

  const handleConfirm = async () => {
    if (!confirmingId) return;
    setActionLoading(true);
    try {
      await axios.patch(
        `${API_URL}/checklist/${confirmingId}/confirm`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchChecklists();
      setConfirmingId(null);
      setNote('');
    } catch (err) {
      alert(err.response?.data?.error || 'Échec de la confirmation');
    } finally {
      setActionLoading(false);
    }
  };

  // How many written observations exist on a checklist — shown on the row so
  // the client can see there is something to open rather than guessing.
  const noteCount = (c) => [
    c.scratchesOrDents, c.dashboardWarningLights, c.personalItemsLeft,
    c.customerRemarks, c.technicianComments, c.reasonOfVisit,
  ].filter((t) => String(t || '').trim()).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8F877B]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400">
        <p className="font-semibold">Erreur</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-6">Historique de réception</h1>

      {checklists.length === 0 ? (
        <div className="bg-[#17140F] border border-[#241F1B] rounded-2xl p-10 text-center">
          <div className="text-6xl mb-4">🚗</div>
          <h2 className="text-lg font-semibold text-white mb-2">Aucune réception enregistrée</h2>
          <p className="text-[#8F877B] text-sm">
            Lorsqu'un atelier réceptionne l'un de vos véhicules, le détail apparaîtra ici.
          </p>
        </div>
      ) : (
        <div className="bg-[#17140F] border border-[#241F1B] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-sm">
              <thead>
                <tr className="border-b border-[#241F1B]">
                  <th className="p-3 w-8" />
                  <th className="p-3 text-left text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Date</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Véhicule</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Atelier</th>
                  <th className="p-3 text-center text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Statut</th>
                  <th className="p-3 text-center text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Constats</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Observations</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#8F877B] uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E1B18]">
                {checklists.map((c) => {
                  const open = openId === c._id;
                  const n = noteCount(c);
                  return (
                    <React.Fragment key={c._id}>
                      <tr
                        className="hover:bg-[#1B1815] transition-colors cursor-pointer"
                        onClick={() => setOpenId(open ? null : c._id)}
                      >
                        <td className="p-3 text-center">
                          <ChevronDown
                            size={15}
                            className={`text-[#8F877B] transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </td>
                        <td className="p-3 text-[#C9C3B6] text-sm whitespace-nowrap">
                          {fmtDate(c.dateReceived || c.createdAt)}
                        </td>
                        <td className="p-3">
                          <div className="font-semibold text-white">{c.vehicleId?.plateNumber || 'N/A'}</div>
                          <div className="text-xs text-[#8F877B]">{c.vehicleId?.brand} {c.vehicleId?.model}</div>
                        </td>
                        <td className="p-3 text-[#C9C3B6] text-sm">
                          {c.workshopId?.details?.businessName || c.workshopId?.businessName || c.workshopId?.name || 'Inconnu'}
                        </td>
                        <td className="p-3 text-center">
                          {c.ownerConfirmation?.confirmed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                              <CheckCircle2 size={11} /> Confirmé
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
                              <AlertCircle size={11} /> En attente
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {c.glassBroken && <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 text-xs rounded-md">Vitre</span>}
                            {c.sideMirrorDamage && <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 text-xs rounded-md">Rétroviseur</span>}
                            {c.headlightDamage && <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 text-xs rounded-md">Phare</span>}
                            {c.tireBoltIssue && <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 text-xs rounded-md">Pneu</span>}
                            {!c.glassBroken && !c.sideMirrorDamage && !c.headlightDamage && !c.tireBoltIssue && (
                              <span className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 text-xs rounded-md">✓ RAS</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-[#C9C3B6] text-sm max-w-xs">
                          {n === 0 ? (
                            <span className="text-[#6E665C]">—</span>
                          ) : (
                            <>
                              <div className="text-xs text-[#C9C3B6] truncate">
                                {c.technicianComments || c.scratchesOrDents || c.customerRemarks || c.reasonOfVisit}
                              </div>
                              {/* The row used to stop here, which is how a
                                  paragraph of observations looked like nothing. */}
                              <div className="text-[10.5px] text-[#DFA23C] mt-0.5">
                                {n} observation{n > 1 ? 's' : ''} — voir le détail
                              </div>
                            </>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {!c.ownerConfirmation?.confirmed && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmingId(c._id); }}
                              className="bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210] hover:from-white hover:to-[#DED9CE] text-xs px-3 py-1.5 rounded-lg transition-colors font-medium"
                            >
                              Confirmer
                            </button>
                          )}
                        </td>
                      </tr>

                      {open && (
                        <tr className="bg-[#131110]">
                          <td colSpan={8} className="p-5">
                            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))] mb-3.5">
                              <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
                                <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">Réception</p>
                                <Row label="Date et heure" value={fmtDateTime(c.dateReceived || c.createdAt)} />
                                <Row label="Réceptionné par" value={c.receivedBy?.name || '—'} />
                                <Row
                                  label="Origine"
                                  value={c.sourceType === 'owner_booking' ? 'Rendez-vous' : 'Sans rendez-vous'}
                                />
                              </div>

                              <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
                                <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">
                                  État à la réception
                                </p>
                                <Row label="Kilométrage" value={km(c.odometer)} />
                                <Row
                                  label="Carburant"
                                  value={c.fuelLevel != null ? `${c.fuelLevel} %` : '—'}
                                />
                                <Row label="Clés remises" value={c.numberOfKeys != null ? c.numberOfKeys : '—'} />
                              </div>

                              <div className="rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3">
                                <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1.5">
                                  État général
                                </p>
                                <Row label="Extérieur" value={fr(c.exteriorCondition)} />
                                <Row label="Intérieur" value={fr(c.interiorCondition)} />
                                <Row label="Pneus" value={fr(c.tireCondition)} />
                              </div>
                            </div>

                            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
                              <NoteBlock title="Motif de la visite" text={c.reasonOfVisit} />
                              <NoteBlock title="Rayures et bosses constatées" text={c.scratchesOrDents} tone="warn" />
                              <NoteBlock title="Voyants au tableau de bord" text={c.dashboardWarningLights} tone="warn" />
                              <NoteBlock title="Effets personnels laissés" text={c.personalItemsLeft} />
                              <NoteBlock title="Vos remarques" text={c.customerRemarks} />
                              <NoteBlock title="Commentaires du technicien" text={c.technicianComments} />
                            </div>

                            {Array.isArray(c.photos) && c.photos.length > 0 && (
                              <div className="mt-3.5">
                                <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-2">
                                  Photos ({c.photos.length})
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {c.photos.map((src, i) => (
                                    <a key={i} href={src} target="_blank" rel="noreferrer">
                                      <img
                                        src={src}
                                        alt={`Réception ${i + 1}`}
                                        className="w-24 h-24 object-cover rounded-lg border border-[#241F1B] hover:border-[#4E4740] transition-colors"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            {c.ownerConfirmation?.confirmed && (
                              <div className="mt-3.5 rounded-xl px-4 py-3 border border-emerald-500/20 bg-emerald-500/5">
                                <p className="text-[10px] uppercase tracking-[.18em] text-emerald-400/70 mb-1.5">
                                  Votre confirmation
                                </p>
                                <p className="text-[12.5px] text-[#C9C3B6]">
                                  Confirmé le {fmtDateTime(c.ownerConfirmation.confirmedAt)}
                                </p>
                                {c.ownerConfirmation.note && (
                                  <p className="text-[12.5px] text-[#F4F1EA] mt-1 whitespace-pre-wrap">
                                    « {c.ownerConfirmation.note} »
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmingId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#17140F] border border-[#241F1B] rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-white mb-3">Confirmer la réception</h3>
            <p className="text-sm text-[#8F877B] mb-4">
              En confirmant, vous reconnaissez l'état du véhicule tel qu'il a été relevé par
              l'atelier. Ouvrez le détail de la ligne avant de confirmer.
            </p>
            <textarea
              className="w-full bg-[#1B1815] border border-[#2B2724] text-white placeholder-[#5C554C] rounded-xl p-3 text-sm mb-4 focus:border-[#4E4740] focus:outline-none resize-none"
              rows="3"
              placeholder="Ajouter une remarque (facultatif)… ex. « tapis de sol manquants »"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setConfirmingId(null); setNote(''); }}
                className="px-4 py-2 text-[#C9C3B6] hover:text-white hover:bg-[#1B1815] rounded-xl text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={actionLoading}
                className="px-4 py-2 bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210] hover:from-white hover:to-[#DED9CE] rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Confirmation…' : 'Confirmer la réception'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceptionHistory;
